/**
 * "What you expected and what happened" (DESIGN-RESULTS §5): the economy against the forecast the year opened with, five cells (policy rate, 10-year yield, unemployment, GDP growth, house prices), each with the forecast's
 * normal range, the forecast, what happened, the gap at year-end and whether it was better or worse for a bank like yours; then one sentence naming the largest miss. The data is `economy.ts`.
 */
import { ChartFrame, type ChartTable } from "../theme/ChartFrame";
import type { ScoreLegs } from "./decomposition";
import { ECONOMY_SERIES, economySummary, pct, type EconomyCell } from "./economy";
import { formatIndexLevel } from "../format";
import { DashedTag } from "./Fold";
import { useElementWidth } from "../theme/useElementWidth";

const W = 210, H = 92, L = 14, T = 8, B = 76;

/** One cell's picture: the band, the dashed forecast, the solid actual with a dot at the year-end; Q1 to Q4 under it. Its own vertical scale. */
function Mini({ cell, fiscal }: { readonly cell: EconomyCell; readonly fiscal: string }) {
  // Drawn at the width of its cell (never wider than W), so a narrow cell on a phone keeps its labels at their size instead of scaling them down.
  const [ref, measured] = useElementWidth<HTMLDivElement>(W);
  const w = Math.min(W, Math.max(120, measured)), R = w - 14;
  const all = [...cell.low, ...cell.high, ...cell.actual, ...cell.forecast];
  const lo = Math.min(...all), hi = Math.max(...all), pad = (hi - lo || Math.abs(hi) * 0.02 || 1) * 0.12;
  const x = (i: number): number => L + (i / 3) * (R - L);
  const y = (v: number): number => B - ((v - (lo - pad)) / (hi - lo + 2 * pad)) * (B - T);
  const path = (vs: readonly number[]): string => vs.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const band = `${cell.high.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ")} ${[...cell.low].reverse().map((v, i) => `L${x(3 - i).toFixed(1)},${y(v).toFixed(1)}`).join(" ")} Z`;
  return (
    <div ref={ref} style={{ maxWidth: W }}><svg viewBox={`0 0 ${w} ${H}`} width="100%" style={{ display: "block", maxWidth: W }} role="img" aria-label={`${cell.title} over ${fiscal}: the forecast ended at ${cell.forecastText}, what happened ended at ${cell.actualText}`} data-testid={`results.economy.${cell.id}.svg`}>
      <path d={band} fill="var(--info-bg)" data-testid={`results.economy.${cell.id}.band`} />
      <path d={path(cell.forecast)} fill="none" stroke="var(--ink-3)" strokeWidth="1.6" strokeDasharray="5 3" data-testid={`results.economy.${cell.id}.forecast`} />
      <path d={path(cell.actual)} fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinejoin="round" data-testid={`results.economy.${cell.id}.actual`} />
      <circle cx={x(3)} cy={y(cell.actual[3] ?? 0)} r="3.6" fill="var(--ink)" stroke="var(--surface)" strokeWidth="1.5" />
      {["Q1", "Q2", "Q3", "Q4"].map((q, i) => <text key={q} x={x(i)} y={H - 4} fontSize={12} textAnchor="middle" fill="var(--ink-3)">{q}</text>)}
    </svg></div>
  );
}

const VERDICT: Readonly<Record<"better" | "worse", { readonly icon: string; readonly words: string }>> = { better: { icon: "▲", words: "Better for a bank like yours" }, worse: { icon: "▼", words: "Worse for a bank like yours" } };

export function EconomyStrip({ cells, fiscal, legs }: { readonly cells: readonly EconomyCell[] | null; readonly fiscal: string; readonly legs: ScoreLegs | null }) {
  const summary = cells === null ? null : economySummary(cells, legs);
  const table: ChartTable | null = cells === null ? null : {
    caption: `The five economy series at the four quarter-ends of ${fiscal}: the forecast, its range and what happened`,
    head: ["Series, quarter-end", "Forecast", "Range", "What happened"],
    rows: cells.flatMap((c) => { const u = c.id === "hpi" ? (v: number) => formatIndexLevel(v) : (v: number) => pct(v); return c.months.map((m, i) => [`${c.title}${c.id === "hpi" ? " (index)" : ""}, Q${i + 1}`, u(c.forecast[i] ?? 0), `${u(c.low[i] ?? 0)} to ${u(c.high[i] ?? 0)}`, u(c.actual[i] ?? 0)]); }),
  };
  return (
    <div className="gx-econ" data-testid="results.economy">
      <div className="gx-econ-head">
      <p className="gx-small gx-faint gx-card-sub">The economy against the published forecast, {fiscal}. The shaded band is the forecast&apos;s normal range: a miss inside it is ordinary.</p>
      <p className="gx-legend gx-small gx-faint gx-econ-legend" data-testid="results.economy.legend">
        <span><i className="gx-legend-dash" />Forecast</span><span><i className="gx-lg-actual" />What happened</span><span><i className="gx-lg-band" />Forecast range</span>
      </p>
      </div>
      {cells === null ? (
        <>
          <div className="gx-econ-cells">{ECONOMY_SERIES.map((s) => <div key={s.id} className="gx-econ-cell" data-testid={`results.economy.${s.id}`}><b className="gx-econ-t">{s.title}</b><div className="gx-econ-big">&ndash;</div></div>)}</div>
          <p className="gx-small gx-faint" data-testid="results.economy.missing">The forecast the year opened with is not stored for this result, so there is nothing to compare the year against.</p>
        </>
      ) : table === null ? null : (
        <ChartFrame summary={`${table.caption}. ${summary?.lead ?? ""}`.trim()} table={table}>
        <div className="gx-econ-cells">
          {cells.map((c) => (
            <div key={c.id} className="gx-econ-cell" data-testid={`results.economy.${c.id}`}>
              <b className="gx-econ-t">{c.title}</b>
              <div className="gx-econ-big"><span data-testid={`results.economy.${c.id}.actualtext`}>{c.actualText}</span> <small>vs {c.forecastText} forecast</small></div>
              <Mini cell={c} fiscal={fiscal} />
              <div className="gx-small gx-faint" data-testid={`results.economy.${c.id}.gap`}>Gap at year-end {c.gapText}</div>
              {c.verdict === "better" || c.verdict === "worse"
                ? <div className={`gx-econ-verdict gx-econ-verdict--${c.verdict}`} data-testid={`results.economy.${c.id}.verdict`}><span aria-hidden="true">{VERDICT[c.verdict].icon}</span> {VERDICT[c.verdict].words}</div>
                : null}
              <div className="gx-small gx-faint" data-testid={`results.economy.${c.id}.effect`}>{c.effect}</div>
            </div>
          ))}
        </div>
        </ChartFrame>
      )}
      {summary === null ? null : (
        <div className="gx-econ-sum" data-testid="results.economy.summary">
          <p><b>{summary.lead}</b>{summary.rest === null ? null : <> {summary.rest}</>}</p>
          <DashedTag>The preview used the published forecast, not what happened</DashedTag>
        </div>
      )}
    </div>
  );
}
